import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Search, Smartphone, User, FileText, X, Loader2, ScanLine } from 'lucide-react';
import BarcodeScanner from './BarcodeScanner';

/**
 * Smart Search Component with live suggestions
 * - Debounced search (300ms)
 * - Live suggestions as you type
 * - Searches devices, customers, sales
 * - Keyboard navigation support
 */
export default function SmartSearch({ placeholder = 'البحث الذكي...', onSelect, autoFocus = false, fullWidth = true, showScanner = true }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim()) {
      setResults(null);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await api.get(`/search/suggest?q=${encodeURIComponent(query)}`);
        setResults(response.data);
        setShowDropdown(true);
      } catch (error) {
        console.error('Search error', error);
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (type, item) => {
    if (onSelect) {
      onSelect(type, item);
    } else {
      // Default: navigate to relevant page
      if (type === 'device') {
        navigate('/inventory');
      } else if (type === 'customer') {
        navigate('/customers');
      } else if (type === 'sale') {
        navigate('/sales');
      }
    }
    setQuery('');
    setResults(null);
    setShowDropdown(false);
  };

  const clearSearch = () => {
    setQuery('');
    setResults(null);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const hasResults = results && (
    results.devices?.length > 0 ||
    results.customers?.length > 0 ||
    results.sales?.length > 0
  );

  return (
    <div className={`relative ${fullWidth ? 'w-full' : ''}`}>
      <div className="relative flex gap-2" ref={inputRef}>
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-5 h-5 pointer-events-none" />
          <Input
            type="text"
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query && results && setShowDropdown(true)}
            autoFocus={autoFocus}
            className="bg-zinc-900 border-zinc-800 text-white pr-10 pl-10 transition-all focus:border-[#007aff]"
            data-testid="smart-search-input"
          />
          {loading && (
            <Loader2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#007aff] w-4 h-4 animate-spin" />
          )}
          {query && !loading && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-white"
              data-testid="clear-search-button"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {showScanner && (
          <Button
            type="button"
            onClick={() => setScannerOpen(true)}
            className="bg-[#007aff] hover:bg-[#0051d5] flex-shrink-0 px-3"
            title="مسح الباركود بالكاميرا"
            data-testid="open-scanner-button"
          >
            <ScanLine className="w-5 h-5" />
          </Button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl shadow-black/50 max-h-96 overflow-y-auto z-50 backdrop-blur-md"
          data-testid="search-suggestions-dropdown"
        >
          {!hasResults && !loading && (
            <div className="p-4 text-center text-zinc-500 text-sm">
              لا توجد نتائج مطابقة لـ "{query}"
            </div>
          )}

          {/* Devices */}
          {results?.devices?.length > 0 && (
            <div className="border-b border-zinc-800">
              <div className="px-3 py-2 bg-zinc-800/50 text-xs text-zinc-400 font-bold flex items-center gap-2">
                <Smartphone className="w-3 h-3" />
                الأجهزة ({results.devices.length})
              </div>
              {results.devices.map((device) => (
                <button
                  key={device.id}
                  onClick={() => handleSelect('device', device)}
                  className="w-full px-3 py-2 hover:bg-zinc-800/50 text-right flex items-center justify-between gap-2 group transition-colors"
                  data-testid="suggestion-device"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-2 h-2 rounded-full ${
                      device.status === 'available' ? 'bg-[#34c759]' : 
                      device.status === 'sold' ? 'bg-[#ff3b30]' : 'bg-[#ff9500]'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">
                        {device.model_name} - {device.capacity} - {device.color || 'بدون لون'}
                      </p>
                      <p className="text-xs text-zinc-500 truncate font-mono">IMEI: {device.imei}</p>
                    </div>
                  </div>
                  <span className="text-xs text-zinc-400 whitespace-nowrap">
                    {device.purchase_price} د
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Customers */}
          {results?.customers?.length > 0 && (
            <div className="border-b border-zinc-800">
              <div className="px-3 py-2 bg-zinc-800/50 text-xs text-zinc-400 font-bold flex items-center gap-2">
                <User className="w-3 h-3" />
                العملاء ({results.customers.length})
              </div>
              {results.customers.map((customer) => (
                <button
                  key={customer.id}
                  onClick={() => handleSelect('customer', customer)}
                  className="w-full px-3 py-2 hover:bg-zinc-800/50 text-right flex items-center gap-3 transition-colors"
                  data-testid="suggestion-customer"
                >
                  <div className="w-8 h-8 rounded-full bg-[#5ac8fa]/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-[#5ac8fa]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">{customer.name}</p>
                    <p className="text-xs text-zinc-500">{customer.phone}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Sales */}
          {results?.sales?.length > 0 && (
            <div>
              <div className="px-3 py-2 bg-zinc-800/50 text-xs text-zinc-400 font-bold flex items-center gap-2">
                <FileText className="w-3 h-3" />
                الفواتير ({results.sales.length})
              </div>
              {results.sales.map((sale) => (
                <button
                  key={sale.id}
                  onClick={() => handleSelect('sale', sale)}
                  className="w-full px-3 py-2 hover:bg-zinc-800/50 text-right transition-colors"
                  data-testid="suggestion-sale"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{sale.customer_name}</p>
                      <p className="text-xs text-zinc-500 font-mono">{sale.invoice_number}</p>
                    </div>
                    <span className="text-xs text-[#34c759] font-bold whitespace-nowrap">
                      {sale.final_price} د
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={(value) => {
          setQuery(value);
          setScannerOpen(false);
        }}
        title="مسح الباركود للبحث"
      />
    </div>
  );
}
